import React from 'react';
import { CalendarClock } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { FenetreEmailsModule } from '../ui/LigneNotification';
import { NotificationBlock, insertNotificationToken } from '../PrestationPlanning/PrestationNotificationsPage';

// Jetons réellement disponibles dans les emails de réponse à une demande d'absence.
const CONGES_TOKENS = [
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{salarie_prenom}}', label: 'Prénom salarié' },
    { token: '{{salarie_nom}}', label: 'Nom salarié' },
    { token: '{{type_absence}}', label: "Type d'absence" },
    { token: '{{date_debut}}', label: 'Date de début' },
    { token: '{{date_fin}}', label: 'Date de fin' },
    { token: '{{jours_accordes}}', label: 'Jours accordés' },
    { token: '{{note}}', label: 'Note (si renseignée)' },
    { token: '{{signataire}}', label: 'Signataire' },
    { token: '{{logo}}', label: 'Votre logo' },
];

/**
 * Réglages des emails du module Demande de congé(s), ouverts directement depuis le module (bouton
 * « Paramètres ») — même principe que les autres modules migrés. Un seul onglet (Salarié) : ces deux
 * emails sont adressés au salarié qui a fait la demande, pas au client ni à l'établissement.
 */
export function CongesEmailSettingsModal({ onClose }: { onClose: () => void }) {
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [erreur, setErreur] = React.useState('');

    const [notifyAccepted, setNotifyAccepted] = React.useState(true);
    const [acceptedSubject, setAcceptedSubject] = React.useState('');
    const [acceptedBody, setAcceptedBody] = React.useState('');
    const [acceptedActiveField, setAcceptedActiveField] = React.useState<'subject' | 'body'>('body');
    const acceptedSubjectRef = React.useRef<HTMLInputElement>(null);
    const acceptedBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [notifyRefused, setNotifyRefused] = React.useState(true);
    const [refusedSubject, setRefusedSubject] = React.useState('');
    const [refusedBody, setRefusedBody] = React.useState('');
    const [refusedActiveField, setRefusedActiveField] = React.useState<'subject' | 'body'>('body');
    const refusedSubjectRef = React.useRef<HTMLInputElement>(null);
    const refusedBodyRef = React.useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        moduleApi.getClientNotificationSettings()
            .then((s: any) => {
                setNotifyAccepted(s ? !!s.notify_conges_accepted : true);
                setAcceptedSubject(s?.conges_accepted_subject || '');
                setAcceptedBody(s?.conges_accepted_body || '');
                setNotifyRefused(s ? !!s.notify_conges_refused : true);
                setRefusedSubject(s?.conges_refused_subject || '');
                setRefusedBody(s?.conges_refused_body || '');
            })
            .catch((e: any) => setErreur(e?.message || 'Erreur de chargement'))
            .finally(() => setLoading(false));
    }, []);

    const insertAcceptedToken = (token: string) => {
        if (acceptedActiveField === 'subject') insertNotificationToken(acceptedSubjectRef.current, acceptedSubject, setAcceptedSubject, token);
        else insertNotificationToken(acceptedBodyRef.current, acceptedBody, setAcceptedBody, token);
    };
    const insertRefusedToken = (token: string) => {
        if (refusedActiveField === 'subject') insertNotificationToken(refusedSubjectRef.current, refusedSubject, setRefusedSubject, token);
        else insertNotificationToken(refusedBodyRef.current, refusedBody, setRefusedBody, token);
    };

    const enregistrer = async () => {
        setSaving(true); setErreur(''); setSaved(false);
        try {
            const res: any = await moduleApi.patchClientNotificationSettings('conges', {
                notify_conges_accepted: notifyAccepted, conges_accepted_subject: acceptedSubject, conges_accepted_body: acceptedBody,
                notify_conges_refused: notifyRefused, conges_refused_subject: refusedSubject, conges_refused_body: refusedBody,
            });
            if (res?.error) { setErreur(res.error); return; }
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'enregistrement.");
        } finally { setSaving(false); }
    };

    return (
        <FenetreEmailsModule titre="Paramètres des emails de demande de congé(s)" sousTitre="Envoyés automatiquement au salarié lorsque sa demande d'absence est traitée." icone={<CalendarClock size={16} />} onClose={onClose}
            chargement={loading} erreur={erreur} enregistrer={enregistrer}
            etat={saving ? 'en_cours' : erreur ? 'echec' : saved ? 'fait' : 'repos'}>
            <NotificationBlock title="Mail d'absence acceptée"
                toggle={{ checked: notifyAccepted, onChange: setNotifyAccepted }}
                subject={acceptedSubject} setSubject={setAcceptedSubject} body={acceptedBody} setBody={setAcceptedBody}
                subjectRef={acceptedSubjectRef} bodyRef={acceptedBodyRef}
                activeField={acceptedActiveField} setActiveField={setAcceptedActiveField}
                onInsertToken={insertAcceptedToken} tokens={CONGES_TOKENS} />
            <NotificationBlock title="Mail d'absence refusée"
                toggle={{ checked: notifyRefused, onChange: setNotifyRefused }}
                subject={refusedSubject} setSubject={setRefusedSubject} body={refusedBody} setBody={setRefusedBody}
                subjectRef={refusedSubjectRef} bodyRef={refusedBodyRef}
                activeField={refusedActiveField} setActiveField={setRefusedActiveField}
                onInsertToken={insertRefusedToken} tokens={CONGES_TOKENS} />
        </FenetreEmailsModule>
    );
}
